import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Search, Send, CheckCircle, AlertCircle, ArrowLeft, Receipt, User, Mail, Phone, Clock, Key } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemandeFacturePage() {
  const { t, formatCurrency, formatDate } = useLanguage();

  const [step, setStep] = useState('search');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [montant, setMontant] = useState('');
  const [heure, setHeure] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const [results, setResults] = useState([]);
  const [selectedVente, setSelectedVente] = useState(null);

  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [newAccount, setNewAccount] = useState(false);
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  const [credentialsSent, setCredentialsSent] = useState(false);
  const [credentialsLoading, setCredentialsLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/facture-request/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ montant: parseFloat(montant), heure, date }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('invoiceRequest.errors.search'));
      if (!data.found || data.results.length === 0) {
        setError(t('invoiceRequest.errors.notFound'));
        setResults([]);
      } else if (data.results.length === 1) {
        setSelectedVente(data.results[0]);
        setStep('form');
      } else {
        setResults(data.results);
        setStep('results');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/facture-request/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ref_vente: selectedVente.ref_vente, email, nom, telephone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('invoiceRequest.errors.send'));
      if (data.status === 'pending') {
        setStep('pending');
      } else {
        setNewAccount(data.new_account);
        setHasExistingAccount(data.has_existing_account);
        setStep('success');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCredentials = async () => {
    setCredentialsLoading(true);
    try {
      const res = await fetch(`${API}/api/facture-request/resend-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telephone, email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('invoiceRequest.errors.generic'));
      setCredentialsSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setCredentialsLoading(false);
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '';
    return formatDate(iso, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Rend une phrase traduite contenant {{email}} en mettant l'e-mail en gras,
  // tout en préservant l'ordre des mots propre à chaque langue (RTL inclus).
  const withEmail = (key) => {
    const parts = t(key).split('{{email}}');
    return (
      <>
        {parts[0]}
        <strong className="text-white">{email}</strong>
        {parts.slice(1).join('{{email}}')}
      </>
    );
  };

  const reset = () => {
    setStep('search');
    setResults([]);
    setSelectedVente(null);
    setError('');
    setNom('');
    setEmail('');
    setTelephone('');
    setNewAccount(false);
    setHasExistingAccount(false);
    setCredentialsSent(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4" data-testid="demande-facture-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="R.E.Mobiles" className="w-20 h-20 mx-auto mb-3 object-contain" />
          <h1 className="text-3xl font-bold text-white tracking-tight">R.E.Mobiles</h1>
          <p className="text-gray-400 mt-1 text-sm">{t('invoiceRequest.subtitle')}</p>
        </div>

        {/* Search */}
        {step === 'search' && (
          <Card className="bg-[#12122a] border-gray-800 shadow-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Search className="w-5 h-5 text-red-500" />
                {t('invoiceRequest.search.title')}
              </CardTitle>
              <p className="text-gray-400 text-sm mt-1">{t('invoiceRequest.search.hint')}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-sm">{t('invoiceRequest.search.amountLabel')}</Label>
                  <Input data-testid="montant-input" type="number" step="0.01" min="0" placeholder={t('invoiceRequest.search.amountPlaceholder')} value={montant} onChange={(e) => setMontant(e.target.value)} required className="bg-[#0d0d1f] border-gray-700 text-white placeholder-gray-500 mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-300 text-sm">{t('invoiceRequest.search.timeLabel')}</Label>
                    <Input data-testid="heure-input" type="time" value={heure} onChange={(e) => setHeure(e.target.value)} required className="bg-[#0d0d1f] border-gray-700 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-300 text-sm">{t('invoiceRequest.search.dateLabel')}</Label>
                    <Input data-testid="date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required className="bg-[#0d0d1f] border-gray-700 text-white mt-1" />
                  </div>
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg" data-testid="search-error">
                    <AlertCircle className="w-4 h-4 shrink-0" />{error}
                  </div>
                )}
                <Button data-testid="search-btn" type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white">
                  {loading ? t('invoiceRequest.search.loading') : t('invoiceRequest.search.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Multiple results */}
        {step === 'results' && (
          <Card className="bg-[#12122a] border-gray-800 shadow-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Receipt className="w-5 h-5 text-red-500" />
                {t('invoiceRequest.results.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {results.map((v) => (
                <button key={v.ref_vente} data-testid={`result-${v.ref_vente}`} onClick={() => { setSelectedVente(v); setStep('form'); }} className="w-full text-left p-3 rounded-lg border border-gray-700 bg-[#0d0d1f] hover:border-red-500 transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">{formatCurrency(v.total || 0)}</span>
                    <span className="text-gray-400 text-xs">{fmtDateTime(v.date_vente)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs">
                    {v.mode_paiement && <span className="text-green-400">{v.mode_paiement}</span>}
                    <span className="text-gray-400">{t('invoiceRequest.results.paidLabel')} {formatCurrency(v.montant_paye ?? v.total ?? 0)}</span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1 truncate">{v.items_summary || t('invoiceRequest.itemsCount', { count: v.items_count })}</p>
                </button>
              ))}
              <Button variant="ghost" onClick={reset} className="w-full text-gray-400 hover:text-white mt-2">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('invoiceRequest.back')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Client info form */}
        {step === 'form' && (
          <Card className="bg-[#12122a] border-gray-800 shadow-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-red-500" />
                {t('invoiceRequest.form.title')}
              </CardTitle>
              {selectedVente && (
                <div className="bg-[#0d0d1f] rounded-lg p-3 mt-2 border border-gray-700 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{t('invoiceRequest.form.purchase')}</span>
                    <span className="text-white font-medium">{formatCurrency(selectedVente.total || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{t('invoiceRequest.form.alreadyPaid')}</span>
                    <span className="text-green-400 font-medium">{formatCurrency(selectedVente.montant_paye ?? selectedVente.total ?? 0)}</span>
                  </div>
                  {selectedVente.mode_paiement && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{t('invoiceRequest.form.payment')}</span>
                      <span className="text-white">{selectedVente.mode_paiement}</span>
                    </div>
                  )}
                  {(selectedVente.reste_a_payer || 0) > 0.01 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{t('invoiceRequest.form.remaining')}</span>
                      <span className="text-amber-400 font-medium">{formatCurrency(selectedVente.reste_a_payer)}</span>
                    </div>
                  )}
                  <p className="text-gray-500 text-xs">{selectedVente.items_summary}</p>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSend} className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-sm">{t('invoiceRequest.form.nameLabel')}</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input data-testid="nom-input" value={nom} onChange={(e) => setNom(e.target.value)} required placeholder={t('invoiceRequest.form.namePlaceholder')} className="bg-[#0d0d1f] border-gray-700 text-white placeholder-gray-500 pl-9" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">{t('invoiceRequest.form.emailLabel')}</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder={t('invoiceRequest.form.emailPlaceholder')} className="bg-[#0d0d1f] border-gray-700 text-white placeholder-gray-500 pl-9" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">{t('invoiceRequest.form.phoneLabel')}</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input data-testid="telephone-input" type="tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} required placeholder={t('invoiceRequest.form.phonePlaceholder')} className="bg-[#0d0d1f] border-gray-700 text-white placeholder-gray-500 pl-9" />
                  </div>
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg" data-testid="form-error">
                    <AlertCircle className="w-4 h-4 shrink-0" />{error}
                  </div>
                )}
                <Button data-testid="send-btn" type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white">
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? t('invoiceRequest.form.sending') : t('invoiceRequest.form.submit')}
                </Button>
                <Button type="button" variant="ghost" onClick={reset} className="w-full text-gray-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" /> {t('invoiceRequest.back')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Pending staff approval */}
        {step === 'pending' && (
          <Card className="bg-[#12122a] border-gray-800 shadow-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2" data-testid="pending-title">{t('invoiceRequest.pending.title')}</h2>
              <p className="text-gray-400 text-sm mb-4">{t('invoiceRequest.pending.desc')}</p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4 text-left" data-testid="pending-info">
                <p className="text-yellow-400 text-sm font-medium mb-1">{t('invoiceRequest.pending.validationRequired')}</p>
                <p className="text-gray-400 text-sm">{withEmail('invoiceRequest.pending.info')}</p>
              </div>
              <Button data-testid="new-request-btn" onClick={reset} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800">{t('invoiceRequest.newRequest')}</Button>
            </CardContent>
          </Card>
        )}

        {/* Success */}
        {step === 'success' && (
          <Card className="bg-[#12122a] border-gray-800 shadow-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2" data-testid="success-title">{t('invoiceRequest.success.title')}</h2>
              <p className="text-gray-400 text-sm mb-4">{withEmail('invoiceRequest.success.desc')}</p>

              {/* New account created → credentials sent automatically */}
              {newAccount && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4 text-left" data-testid="new-account-info">
                  <p className="text-green-400 text-sm font-medium mb-1">{t('invoiceRequest.success.accountCreated')}</p>
                  <p className="text-gray-400 text-sm">{withEmail('invoiceRequest.success.newAccountDesc')}</p>
                </div>
              )}

              {/* Existing account → button to resend credentials */}
              {hasExistingAccount && !credentialsSent && (
                <Button
                  data-testid="resend-credentials-btn"
                  onClick={handleResendCredentials}
                  disabled={credentialsLoading}
                  variant="outline"
                  className="w-full border-cyan-600 text-cyan-400 hover:bg-cyan-600/10 mb-3"
                >
                  <Key className="w-4 h-4 mr-2" />
                  {credentialsLoading ? t('invoiceRequest.success.sendingCredentials') : t('invoiceRequest.success.resendCredentials')}
                </Button>
              )}

              {credentialsSent && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mb-4 text-left" data-testid="credentials-sent-info">
                  <p className="text-cyan-400 text-sm">{withEmail('invoiceRequest.success.credentialsSent')}</p>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button data-testid="new-request-btn" onClick={reset} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800">{t('invoiceRequest.newRequest')}</Button>
                <a href="/espace-client" data-testid="portal-link" className="inline-flex items-center justify-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors">
                  <User className="w-4 h-4" /> {t('invoiceRequest.success.trackRepairs')}
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center mt-6 text-gray-500 text-xs">{t('invoiceRequest.footer')}</div>
      </div>
    </div>
  );
}
